import { useCallback } from 'react';

import { isNil } from 'lodash';
import { useHistory, useLocation } from 'react-router';

/**
 * Custom hook to set multiple search parameters at once
 * @returns An object with a function to update multiple URL parameters atomically
 */
export function useBatchSearchParams() {
    const location = useLocation();
    const history = useHistory();

    /**
     * Updates multiple URL search parameters in a single operation
     * @param params - Record of parameter names to values
     * @param replace - Whether to replace the current history entry (default: false)
     */
    const setMultipleParams = useCallback(
        (params: Record<string, string | undefined>, replace = false) => {
            const newSearchParams = new URLSearchParams(location.search);

            // Update all parameters
            Object.entries(params).forEach(([key, value]) => {
                if (!isNil(value)) {
                    newSearchParams.set(key, value);
                } else {
                    newSearchParams.delete(key);
                }
            });

            const search = newSearchParams.toString();
            const newUrl = search ? `${location.pathname}?${search}` : location.pathname;

            // Update URL without adding to history if replace is true
            if (replace) {
                history.replace(newUrl);
            } else {
                history.push(newUrl);
            }
        },
        [location, history]
    );

    return { setMultipleParams };
}
